import { useTheme } from '@/context/ThemeContext';

const layers = [
  {
    icon: 'ri-mic-2-line',
    name: 'AI Voice Layer',
    desc: 'Answers calls instantly, books appointments, and handles inquiries 24/7',
    color: '#38D6D6',
  },
  {
    icon: 'ri-radar-line',
    name: 'Lead Capture Layer',
    desc: 'Website chat, SMS, and forms unified into one system',
    color: '#4FA3FF',
  },
  {
    icon: 'ri-settings-3-line',
    name: 'Automation Layer',
    desc: 'Follow-ups, reminders, and nurture sequences run without manual input',
    color: '#38D6D6',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    name: 'Pipeline Layer',
    desc: 'Organized CRM tracking every opportunity from first touch to close',
    color: '#4FA3FF',
  },
  {
    icon: 'ri-focus-3-line',
    name: 'Conversion Layer',
    desc: 'Ensures every lead is followed through to close with zero leakage',
    color: '#38D6D6',
  },
];

const bullets = [
  { icon: 'ri-mic-line', text: 'AI Voice Receptionist answers every call' },
  { icon: 'ri-message-3-line', text: 'SMS and chat capture across all channels' },
  { icon: 'ri-calendar-check-line', text: 'Instant booking and scheduling automation' },
  { icon: 'ri-route-line', text: 'Smart qualification and routing' },
  { icon: 'ri-loop-right-line', text: 'Automated follow-ups that convert leads' },
];

export default function SolutionSection() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section
      id="solution"
      className={`relative py-28 md:py-36 overflow-hidden transition-colors duration-500 ${isDark ? 'bg-black' : 'bg-[#F5F5F7]'}`}
    >
      <div className={`absolute top-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-[#38D6D6]/20 to-transparent' : 'bg-gradient-to-r from-transparent via-[#38D6D6]/30 to-transparent'}`} />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-20">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-px bg-[#38D6D6]/60"></div>
            <span className="text-xs font-grotesk text-[#38D6D6] tracking-widest uppercase">The Solution</span>
          </div>
          <h2 className={`font-sora font-800 text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-7 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>
            Install a System That<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]">
              Captures Everything
            </span>
          </h2>
          <p className={`font-grotesk text-lg leading-relaxed ${isDark ? 'text-white/55' : 'text-[#1D1D1F]/55'}`}>
            AI Prime Systems installs intelligent infrastructure that ensures every lead is captured, responded to, and converted automatically. This is not a chatbot. This is a complete revenue capture system built for operational reliability.
          </p>
        </div>

        {/* Full-bleed visual + bullets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20 items-center">
          {/* Visual */}
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/644f99789274670ef493dd7bda2b0dc8.jpg"
              alt="AI Revenue Capture System visualization"
              className="w-full h-[380px] object-cover object-center"
            />
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-black/60 via-transparent to-transparent' : 'bg-gradient-to-r from-[#F5F5F7]/60 via-transparent to-transparent'}`}></div>
            {/* Floating badge */}
            <div className="absolute bottom-6 left-6 right-6">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border backdrop-blur-md ${isDark ? 'border-[#38D6D6]/30 bg-black/60' : 'border-[#38D6D6]/30 bg-white/60'}`}>
                <span className="w-2 h-2 rounded-full bg-[#38D6D6] animate-pulse flex-shrink-0"></span>
                <span className={`text-xs font-grotesk ${isDark ? 'text-white/80' : 'text-black/80'}`}>System Active — All Channels Monitored</span>
              </div>
            </div>
          </div>

          {/* Bullets */}
          <div className="flex flex-col gap-4">
            {bullets.map((item, i) => (
              <div
                key={i}
                className={`flex items-center gap-4 p-5 rounded-xl border transition-all duration-300 ${
                  isDark
                    ? 'bg-white/[0.02] border-white/5 hover:border-[#38D6D6]/20'
                    : 'bg-white border-black/5 hover:border-[#38D6D6]/25'
                }`}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#38D6D6]/10 border border-[#38D6D6]/20 flex-shrink-0">
                  <i className={`${item.icon} text-[#38D6D6] text-base`}></i>
                </div>
                <span className={`font-grotesk text-base ${isDark ? 'text-white/75' : 'text-[#1D1D1F]/75'}`}>{item.text}</span>
                <div className="ml-auto w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-[#38D6D6] text-sm"></i>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System layers */}
        <div>
          <h3 className={`font-sora font-700 text-2xl md:text-3xl mb-10 text-center ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>
            The AI Revenue Capture System
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {layers.map((layer, i) => (
              <div
                key={i}
                className={`group p-6 rounded-2xl border transition-all duration-300 hover:-translate-y-1.5 flex flex-col items-center text-center cursor-default ${
                  isDark
                    ? 'bg-[#0E1621] border-white/5 hover:border-[#38D6D6]/25'
                    : 'bg-white border-black/5 hover:border-[#38D6D6]/25'
                }`}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl mb-4 transition-all"
                  style={{ background: `${layer.color}15`, border: `1px solid ${layer.color}25` }}
                >
                  <i className={`${layer.icon} text-xl`} style={{ color: layer.color }}></i>
                </div>
                <h4 className={`font-sora font-600 text-sm mb-2 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`}>{layer.name}</h4>
                <p className={`font-grotesk text-xs leading-relaxed ${isDark ? 'text-white/40' : 'text-black/40'}`}>{layer.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`absolute bottom-0 left-0 w-full h-px ${isDark ? 'bg-gradient-to-r from-transparent via-white/5 to-transparent' : 'bg-gradient-to-r from-transparent via-black/5 to-transparent'}`} />
    </section>
  );
}
